import { FunctionComponent } from 'react'
import { Chip, Stack, Typography } from '@mui/material'
import {
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'

interface AutoSaveIndicatorProps {
  isDirty: boolean
  isAutoSaving?: boolean
  lastSavedTime?: Date | null
  showLabel?: boolean
}

const AutoSaveIndicator: FunctionComponent<AutoSaveIndicatorProps> = ({
  isDirty,
  isAutoSaving = false,
  lastSavedTime,
  showLabel = true
}) => {
  const formatLastSaved = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const seconds = Math.floor(diff / 1000)

    if (seconds < 10) return 'przed chwilą'
    if (seconds < 60) return `${seconds}s temu`

    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}min temu`

    const hours = Math.floor(minutes / 60)
    return `${hours}h temu`
  }

  if (isAutoSaving) {
    return (
      <Chip
        icon={<SaveIcon className='pulsing' />}
        label={showLabel ? 'Zapisywanie...' : ''}
        color='info'
        size='small'
        sx={{
          fontSize: '11px',
          height: '24px',
          '& .pulsing': {
            animation: 'pulse 1.5s ease-in-out infinite'
          }
        }}
      />
    )
  }

  if (isDirty) {
    return (
      <Chip
        icon={<ScheduleIcon />}
        label={showLabel ? 'Niezapisane' : ''}
        color='warning'
        size='small'
        sx={{ fontSize: '11px', height: '24px' }}
      />
    )
  }

  if (lastSavedTime) {
    return (
      <Stack direction='row' alignItems='center' spacing={0.5}>
        <CheckCircleIcon color='success' sx={{ fontSize: 16 }} />
        {showLabel && (
          <Typography variant='caption' color='success.main' sx={{ fontSize: '11px' }}>
            Zapisano {formatLastSaved(lastSavedTime)}
          </Typography>
        )}
      </Stack>
    )
  }

  return null
}

export default AutoSaveIndicator

// Add CSS for pulsing animation
export const AutoSaveStyles = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`
